import React, { ReactElement, useEffect, useState } from "react";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { RadioButton } from "primereact/radiobutton";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { formatCurrency, formatNumber, formatNumberWithDecimal } from "../../../utils/commonUtil";
import { useFilterSelection } from "../FilterSelectionContext";

const KeyMemberAtGlance: React.FC<{ entityId: string }> = ({ entityId }) => {
  const trackerData = useSelector((state: RootState) => state.myTracker.trackerData);
  const { isShowingTotals } = useFilterSelection();
  const [isActual, setIsActual] = useState(true);
  const [keyGlanceDetail, setKeyGlanceDetail] = useState({
    LVR: 0,
    DSR: 0,
    YieldPurchasePrice: 0,
    YieldCurrentValue: 0,
    TotalProperties: 0,
    AverageAnnualCapitalGrowth: 0,
    CurrentPortfolioValue: 0,
  });
  const [dataExists, setDataExists] = useState<boolean>(true);

  type TotalPropertiesType = {
    LVR: string;
    DSR: string;
    YieldVsPurchasePrice: string;
    YieldVsCurrentValuation: string;
    NoOfProperties: string;
    AverageCapitalGrowth: string;
    TotalValue: string;
  };

  useEffect(() => {
    if (trackerData) {
      let data =
        entityId === "0"
          ? trackerData.find((item) => item.EntityId === "")
          : trackerData.find((item) => item.EntityId === entityId);

      if (data) {
        let totalProperties: TotalPropertiesType;
        if (isShowingTotals) {
          totalProperties = isActual ? data.KeyNumberActualsTotal : data.KeyNumberForecastTotal;
        } else {
          totalProperties = isActual ? data.KeyNumberActualsPortion : data.KeyNumberForecastPortion;
        }

        const requiredFields: (keyof TotalPropertiesType)[] = [
          "AverageCapitalGrowth",
          "NoOfProperties",
          "TotalValue",
          "LVR",
          "DSR",
          "YieldVsPurchasePrice",
          "YieldVsCurrentValuation",
        ];
        setDataExists(
          requiredFields.every(
            (field) =>
              totalProperties[field] !== null || totalProperties[field] !== undefined || totalProperties[field] !== "0"
          )
        );
        setKeyGlanceDetail({
          LVR: parseFloat(totalProperties.LVR),
          DSR: parseFloat(totalProperties.DSR),
          YieldPurchasePrice: parseFloat(totalProperties.YieldVsPurchasePrice),
          YieldCurrentValue: parseFloat(totalProperties.YieldVsCurrentValuation),
          TotalProperties: parseFloat(totalProperties.NoOfProperties),
          AverageAnnualCapitalGrowth: parseFloat(totalProperties.AverageCapitalGrowth),
          CurrentPortfolioValue: parseFloat(totalProperties.TotalValue),
        });
      } else {
        setDataExists(false);
      }
    }
  }, [trackerData, entityId, isActual]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value == null || value === undefined || value === 0
        ? formatCurrency(Number("0.00"))
        : value < 0
          ? `(${formatCurrency(-value)})`
          : formatCurrency(value)}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600 " : ""}>
      {value == null || value === undefined || value === 0
        ? "0.00%"
        : value < 0
          ? `(${formatNumberWithDecimal(-value, 2, 2)}%)`
          : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const headerTemplate = (options: any): ReactElement<any, any> => {
    const className = `${options.className} justify-content-space-between bg-slate-600 text-white`;

    return (
      <div className={className}>
        <div className="flex align-items-center gap-2">
          <span className="font-bold">Key numbers at a glance – current view</span>
        </div>
        <div className="flex align-items-center gap-2 mr-4">
          <RadioButton
            inputId="forcastType"
            data-testid="mtkmg-rdo-actual"
            name="forcastType"
            value={isActual}
            onChange={(e) => {
              setIsActual(!e.target.value);
            }}
            checked={isActual}
          />
          <label htmlFor="purachaseValueinPercentage" className="ml-1">
            Actual
          </label>

          <RadioButton
            inputId="forcastType"
            data-testid="mtkmg-rdo-forecast"
            name="forcastType"
            value={!isActual}
            onChange={(e) => {
              setIsActual(e.target.value);
            }}
            checked={!isActual}
          />
          <label htmlFor="purachaseValueinPercentage" className="ml-1">
            Forecast
          </label>
        </div>
      </div>
    );
  };

  const GlanceDetail = () => (
    <div>
      <div className="flex flex-row content-center text-center font-bold">
        <>
          {!dataExists && (
            <>
              <div className="w-1/6">--%</div>
              <div className="w-1/6">--%</div>
              <div className="w-1/6">--%</div>
              <div className="w-1/6">--%</div>
              <div className="w-1/6">--</div>
              <div className="w-1/6">--%</div>
              <div className="w-1/6">$--</div>
            </>
          )}
          {dataExists && (
            <>
              <div className="w-1/6">{keyGlanceDetail.LVR ? percentageTemplate(keyGlanceDetail.LVR) : "0.00"}</div>
              <div className="w-1/6">
                <span className={keyGlanceDetail.DSR < 0 ? "text-red-600" : ""}>
                  {keyGlanceDetail.DSR < 0
                    ? `(${formatNumberWithDecimal(-keyGlanceDetail.DSR, 2, 2)})`
                    : `${formatNumberWithDecimal(keyGlanceDetail.DSR, 2, 2)}`}
                </span>
              </div>
              <div className="w-1/6">
                {keyGlanceDetail.YieldPurchasePrice ? percentageTemplate(keyGlanceDetail.YieldPurchasePrice) : "0.00%"}
              </div>
              <div className="w-1/6">
                {keyGlanceDetail.YieldCurrentValue ? percentageTemplate(keyGlanceDetail.YieldCurrentValue) : "0.00%"}
              </div>
              <div className="w-1/6">
                {keyGlanceDetail.TotalProperties ? formatNumber(keyGlanceDetail.TotalProperties) : "--"}
              </div>
              <div className="w-1/6">
                {keyGlanceDetail.AverageAnnualCapitalGrowth
                  ? percentageTemplate(keyGlanceDetail.AverageAnnualCapitalGrowth)
                  : "0.00%"}
              </div>
              <div className="w-1/6">
                {keyGlanceDetail.CurrentPortfolioValue
                  ? currencyTemplate(keyGlanceDetail.CurrentPortfolioValue)
                  : "$0.00"}
              </div>
            </>
          )}
        </>
      </div>

      <div className="flex flex-row content-center mt-2 text-center">
        {["LVR", "DSR", "Yield %", "Yield %", "Properties", "Average annual", "Current"].map((label, index) => (
          <div key={index} className="w-1/6">
            {label}
          </div>
        ))}
      </div>
      <div className="flex flex-row content-center text-center">
        {[
          "(loan to value ratio)",
          "(debt service ratio)",
          "(of purchase price)",
          "(of current valuation)",
          "in portfolio",
          "capital growth",
          "portfolio value",
        ].map((subLabel, index) => (
          <div key={index} className="w-1/6">
            {subLabel}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {trackerData && (
        <REIToggablePanel
          header=""
          istoggleable={false}
          defaultCollapse={false}
          customHeader={headerTemplate}
          contentComponent={<GlanceDetail />}
        />
      )}
    </>
  );
};

export default KeyMemberAtGlance;
